/**
 * Option C's three-way merge (docs/architecture/concurrent-edits.md): after a save is refused because
 * someone else saved first, bring their changes into the form without losing
 * anything the reader typed.
 *
 * Per field, against `base` (what the form loaded):
 * - the reader left it alone → take theirs;
 * - they left it alone → keep the reader's;
 * - both changed it to different values → keep the reader's and mark a clash,
 *   which the screen shows with their value and a "Use …'s" action.
 */
export type StaleFieldStatus = 'same' | 'theirs' | 'mine' | 'clash';

export interface StaleMerge<T> {
  merged: T;
  status: Record<keyof T & string, StaleFieldStatus>;
}

/**
 * Empty is empty: a cleared text field (`''`), `null` and `undefined` are the
 * same value to a reader, and treating them as different would invent clashes
 * between a form's `''` and a read's `null`. Dates and arrays compare by value.
 */
export function sameValue(a: unknown, b: unknown): boolean {
  const norm = (v: unknown) => (v === '' || v === undefined ? null : v);
  const x = norm(a);
  const y = norm(b);
  if (x === y) return true;
  if (x === null || y === null) return false;
  if (x instanceof Date || y instanceof Date) {
    return new Date(x as Date).getTime() === new Date(y as Date).getTime();
  }
  if (typeof x === 'object' && typeof y === 'object') {
    return JSON.stringify(x) === JSON.stringify(y);
  }
  return false;
}

export function mergeStaleEdit<T extends Record<string, unknown>>(
  base: T,
  mine: T,
  theirs: T,
  fields: readonly (keyof T & string)[],
  equals: (a: unknown, b: unknown) => boolean = sameValue,
): StaleMerge<T> {
  const merged = { ...mine };
  const status = {} as Record<keyof T & string, StaleFieldStatus>;
  for (const field of fields) {
    const iChanged = !equals(mine[field], base[field]);
    const theyChanged = !equals(theirs[field], base[field]);
    if (!iChanged) {
      merged[field] = theirs[field];
      status[field] = theyChanged ? 'theirs' : 'same';
    } else if (!theyChanged || equals(mine[field], theirs[field])) {
      status[field] = theyChanged ? 'same' : 'mine';
    } else {
      status[field] = 'clash';
    }
  }
  return { merged, status };
}
